import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, GraduationCap, Headphones, LifeBuoy, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";

const FEATURES = [
  { icon: Coins, t: ["Learn and Earn", "सीखें और कमाएँ"], d: ["Students earn from the real products they design and build.", "छात्र अपने बनाए असली उत्पादों से कमाते हैं।"], to: "/learn-and-earn" },
  { icon: GraduationCap, t: ["Teacher Training", "शिक्षक प्रशिक्षण"], d: ["Certified trainers and hands-on workshops for school staff.", "स्कूल स्टाफ़ के लिए प्रमाणित ट्रेनर और हैंड्स-ऑन वर्कशॉप।"], to: "/schools" },
  { icon: Headphones, t: ["Expert Assistance", "विशेषज्ञ सहायता"], d: ["Engineers mentor every project from idea to demo day.", "इंजीनियर हर प्रोजेक्ट को आइडिया से डेमो डे तक गाइड करते हैं।"], to: "/contact" },
  { icon: LifeBuoy, t: ["24/7 Help Desk", "24/7 हेल्प डेस्क"], d: ["Round-the-clock support for schools, parents and students.", "स्कूलों, अभिभावकों और छात्रों के लिए चौबीसों घंटे सहायता।"], to: "/contact" },
];

const KITS = [
  { img: "/products/kits/arduino.jpg", t: ["Arduino Starter Kit", "Arduino स्टार्टर किट"], d: ["Boards, sensors & 30+ guided builds", "बोर्ड, सेंसर और 30+ गाइडेड प्रोजेक्ट"] },
  { img: "/products/kits/drone.jpg", t: ["Drone Development Kit", "ड्रोन डेवलपमेंट किट"], d: ["Frame, motors, flight controller", "फ़्रेम, मोटर, फ़्लाइट कंट्रोलर"] },
  { img: "/products/kits/cansat.jpg", t: ["CanSat Parachute Kit", "CanSat पैराशूट किट"], d: ["Mini-satellite with telemetry", "टेलीमेट्री के साथ मिनी-सैटेलाइट"] },
  { img: "/products/kits/humanoid.jpg", t: ["Humanoid Robot Kit", "ह्यूमनॉइड रोबोट किट"], d: ["Servo skeleton + AI controller", "सर्वो ढाँचा + AI कंट्रोलर"] },
];

export const FeatureCards = () => {
  const { L } = useLang();
  return (
    <section className="bg-[#E8F0FE] text-[#0A192F] pb-16 pt-4 -mt-px" data-testid="home-features">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {FEATURES.map((f, i) => (
          <Reveal key={f.t[0]} delay={0.07 * i}>
            <Link to={f.to} className="group block h-full p-6 rounded-2xl bg-white border border-slate-200 shadow-[0_8px_30px_rgba(10,25,47,0.06)] hover:border-[#00B8D4]/60 hover:bg-white hover:-translate-y-1 transition-[transform,background-color,border-color] duration-300" data-testid={`feature-card-${i}`}>
              <f.icon className="w-8 h-8 text-[#FF2E63]" />
              <h3 className="mt-4 font-bold text-lg flex items-center gap-1">{L(...f.t)}<ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" /></h3>
              <p className="mt-1 text-sm text-slate-600">{L(...f.d)}</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

export const SolutionsRow = () => {
  const { L } = useLang();
  return (
    <section className="bg-[#F0F4F8] text-[#0A192F] py-20" data-testid="home-solutions">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <span className="rs-chip">{L("Robotics Solutions", "रोबोटिक्स समाधान")}</span>
            <h2 className="text-3xl md:text-4xl font-extrabold mt-4">{L("Kits that turn classrooms into labs", "किट जो कक्षाओं को लैब बना दें")}</h2>
          </div>
          <Link to="/roboshop" className="text-[#007A8A] font-semibold inline-flex items-center gap-1 py-2" data-testid="solutions-shop-link">{L("Visit Roboshop", "रोबोशॉप देखें")} <ArrowUpRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {KITS.map((k, i) => (
            <Reveal key={k.t[0]} delay={0.07 * i}>
              <Link to="/roboshop" className="group block rounded-2xl overflow-hidden border border-slate-200 bg-white hover:shadow-[0_0_40px_rgba(255,46,99,0.2)] transition-shadow duration-500" data-testid={`solution-kit-${i}`}>
                <div className="aspect-[4/3] overflow-hidden"><img src={k.img} alt={k.t[0]} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
                <div className="p-5"><h3 className="font-bold">{L(...k.t)}</h3><p className="text-xs text-slate-500 mt-1">{L(...k.d)}</p></div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const CLIPS = [
  { src: "/videos/motion-1.mp4", poster: "/videos/motion-1.jpg", t: ["Circuits", "सर्किट"] },
  { src: "/videos/motion-2.mp4", poster: "/videos/motion-2.jpg", t: ["Drones", "ड्रोन"] },
  { src: "/videos/motion-3.mp4", poster: "/videos/motion-3.jpg", t: ["Robotic Arms", "रोबोटिक आर्म"] },
  { src: "/videos/motion-4.mp4", poster: "/videos/motion-4.jpg", t: ["CanSat", "CanSat"] },
  { src: "/videos/motion-5.mp4", poster: "/videos/motion-5.jpg", t: ["Showcase", "प्रदर्शन"] },
];

export const MotionBand = () => {
  const { L } = useLang();
  const v = useRef(null);
  const box = useRef(null);
  const [i, setI] = useState(0);
  const [pct, setPct] = useState(0);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const el = v.current;
    const io = new IntersectionObserver(([e]) => (e.isIntersecting ? el.play().catch(() => {}) : el.pause()), { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  useEffect(() => { const el = v.current; el.load(); el.play().catch(() => {}); }, [i]);

  const move = (e) => {
    const r = box.current.getBoundingClientRect();
    setTilt({ x: ((e.clientY - r.top) / r.height - 0.5) * -4, y: ((e.clientX - r.left) / r.width - 0.5) * 6 });
  };
  const c = CLIPS[i];
  return (
    <section className="bg-[#E8F0FE] text-[#0A192F] py-20 overflow-hidden" data-testid="home-motion">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="mb-8">
          <span className="rs-chip">{L("RoboSpark in Motion", "RoboSpark इन मोशन")}</span>
          <h2 className="text-3xl md:text-4xl font-extrabold mt-4">{L("30 seconds inside our labs", "हमारी लैब के अंदर 30 सेकंड")}</h2>
        </div>
        <div style={{ perspective: "1400px" }}>
          <div ref={box} onMouseMove={move} onMouseLeave={() => setTilt({ x: 0, y: 0 })}
            style={{ transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`, transition: "transform 0.4s ease-out" }}
            className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-[0_40px_120px_-30px_rgba(10,25,47,0.35)] bg-[#D6E6FA] aspect-video">
            <video ref={v} key="motion" className="w-full h-full object-cover" muted autoPlay playsInline preload="auto" poster={c.poster}
              onEnded={() => setI((i + 1) % CLIPS.length)} onTimeUpdate={(e) => setPct((e.target.currentTime / (e.target.duration || 6)) * 100)}
              data-testid="motion-video" aria-label={`RoboSpark clip: ${c.t[0]}`}>
              <source src={c.src} type="video/mp4" />
            </video>
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#E8F0FE]/70 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 flex flex-wrap gap-2">
              {CLIPS.map((x, j) => (
                <button key={x.src} onClick={() => setI(j)} data-testid={`motion-chip-${j}`}
                  className={`relative overflow-hidden px-4 py-2 rounded-full text-xs font-bold backdrop-blur-md border transition-colors ${j === i ? "border-[#FF2E63] bg-white text-[#0A192F]" : "border-white bg-white/70 text-[#0A192F]/80 hover:text-[#0A192F] hover:bg-white"}`}>
                  {j === i && <span className="absolute inset-y-0 left-0 bg-[#00E5FF]/45" style={{ width: `${pct}%` }} />}
                  <span className="relative">{L(...x.t)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <link rel="preload" as="video" href={CLIPS[(i + 1) % CLIPS.length].src} />
      </div>
    </section>
  );
};
